/**
 * Cache-friendly BVH for ray queries against an indexed triangle mesh.
 *
 * Layout (struct-of-arrays, all flat typed arrays — no per-node JS objects):
 *   - `nodeBounds`: 6 floats per node (minX, minY, minZ, maxX, maxY, maxZ)
 *   - `nodeLeftFirst`: int32 per node. Internal node → left child node index
 *                     (right child is implicit at `leftChild + 1`). Leaf node →
 *                     first triangle index in `triIds`.
 *   - `nodeTriCount`:  uint32 per node. 0 → internal node. >0 → leaf with that
 *                     many triangles starting at `nodeLeftFirst`.
 *   - `triIds`:        reordered triangle IDs. Each leaf references a
 *                     contiguous range `[first, first + count)`.
 *
 * Triangle data is external: the BVH stores indices into a (positions, indices)
 * pair the caller owns. A triangle with ID `t` reads its three vertex indices
 * from `indices[t*3 .. t*3+3]`, then those into `positions[v*3 .. v*3+3]`.
 *
 * Build is top-down binned SAH. Queries descend via an explicit stack with
 * front-to-back ordering by AABB near-t, culling subtrees whose AABB exceeds
 * the current best hit distance.
 */

export interface Bvh {
  readonly nodeCount: number;
  readonly nodeBounds: Float32Array;
  readonly nodeLeftFirst: Int32Array;
  readonly nodeTriCount: Uint32Array;
  readonly triIds: Uint32Array;
  /** Reference to the source position buffer (read-only at query time). */
  readonly positions: Float32Array;
  /** Reference to the source index buffer (read-only at query time). */
  readonly indices: Uint32Array | Uint16Array;
  /** World AABB of the root (= node 0's bounds). Convenience for TLAS instance bounds. */
  readonly rootBounds: { min: [number, number, number]; max: [number, number, number] };
}

export const BVH_LEAF_TRI_THRESHOLD = 4;
